import { forwardRef, type ButtonHTMLAttributes, type InputHTMLAttributes, type ReactNode, type SelectHTMLAttributes, type TextareaHTMLAttributes } from "react";
import type {
  CommissionStatus,
  LeadStatus,
  LicenseStatus,
  OrderStatus,
  ProjectStatus,
  SubscriptionStatus,
} from "../lib/types";

export function cx(...parts: Array<string | false | null | undefined>): string {
  return parts.filter(Boolean).join(" ");
}

type ButtonVariant = "primary" | "secondary" | "ghost" | "danger";

const BUTTON_VARIANTS: Record<ButtonVariant, string> = {
  primary: "bg-accent text-base font-semibold hover:bg-accent/90",
  secondary: "bg-raised text-ink border border-hairline-strong hover:border-accent/60",
  ghost: "text-muted hover:text-ink",
  danger: "bg-danger/15 text-danger border border-danger/40 hover:bg-danger/25",
};

interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  variant?: ButtonVariant;
}

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", className, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      className={cx(
        "inline-flex items-center justify-center gap-2 rounded-md px-3 py-1.5 text-sm transition-colors",
        "disabled:cursor-not-allowed disabled:opacity-50",
        BUTTON_VARIANTS[variant],
        className,
      )}
      {...rest}
    />
  );
});

export const Input = forwardRef<HTMLInputElement, InputHTMLAttributes<HTMLInputElement>>(
  function Input({ className, ...rest }, ref) {
    return (
      <input
        ref={ref}
        className={cx(
          "w-full rounded-md border border-hairline-strong bg-base px-3 py-2 text-sm text-ink",
          "placeholder:text-dim focus:border-accent",
          className,
        )}
        {...rest}
      />
    );
  },
);

/** Same skin as Input, for the fields that need more than one line. */
export const Textarea = forwardRef<
  HTMLTextAreaElement,
  TextareaHTMLAttributes<HTMLTextAreaElement>
>(function Textarea({ className, ...rest }, ref) {
  return (
    <textarea
      ref={ref}
      className={cx(
        "w-full rounded-md border border-hairline-strong bg-base px-3 py-2 text-sm text-ink",
        "placeholder:text-dim focus:border-accent",
        className,
      )}
      {...rest}
    />
  );
});

export const Select = forwardRef<HTMLSelectElement, SelectHTMLAttributes<HTMLSelectElement>>(
  function Select({ className, ...rest }, ref) {
    return (
      <select
        ref={ref}
        className={cx(
          "w-full rounded-md border border-hairline-strong bg-base px-3 py-2 text-sm text-ink focus:border-accent",
          className,
        )}
        {...rest}
      />
    );
  },
);

export function Field({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="block space-y-1.5">
      <span className="field-label">{label}</span>
      {children}
      {hint !== undefined && <span className="block text-xs text-dim">{hint}</span>}
    </label>
  );
}

/**
 * Status carries the most weight on the licence list, so it is the one place
 * colour is used to mean something rather than to decorate. Every badge also
 * says the status in words — colour alone would exclude anyone who cannot
 * distinguish these hues.
 */
const STATUS_STYLES: Record<LicenseStatus, string> = {
  active: "bg-live/15 text-live border-live/40",
  trialing: "bg-accent/15 text-accent border-accent/40",
  past_due: "bg-pending/15 text-pending border-pending/40",
  suspended: "bg-pending/15 text-pending border-pending/40",
  revoked: "bg-danger/15 text-danger border-danger/40",
  expired: "bg-white/5 text-dim border-hairline-strong",
};

export function StatusBadge({ status }: { status: LicenseStatus }) {
  return (
    <span
      className={cx(
        "inline-block rounded border px-2 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wider",
        STATUS_STYLES[status],
      )}
    >
      {status.replace("_", " ")}
    </span>
  );
}

type Tone = "live" | "accent" | "pending" | "danger" | "quiet";

const TONES: Record<Tone, string> = {
  live: "bg-live/15 text-live border-live/40",
  accent: "bg-accent/15 text-accent border-accent/40",
  pending: "bg-pending/15 text-pending border-pending/40",
  danger: "bg-danger/15 text-danger border-danger/40",
  quiet: "bg-white/5 text-dim border-hairline-strong",
};

export function Badge({ tone, children }: { tone: Tone; children: ReactNode }) {
  return (
    <span
      className={cx(
        "inline-block whitespace-nowrap rounded border px-2 py-0.5 text-[0.6875rem] font-medium uppercase tracking-wider",
        TONES[tone],
      )}
    >
      {children}
    </span>
  );
}

const ORDER_TONES: Record<OrderStatus, Tone> = {
  draft: "quiet",
  awaiting_payment: "pending",
  paid: "live",
  cancelled: "quiet",
  refunded: "danger",
};

export function OrderStatusBadge({ status }: { status: OrderStatus }) {
  return <Badge tone={ORDER_TONES[status]}>{status.replace("_", " ")}</Badge>;
}

/** `new` wants attention; `contacted` is in hand; `closed` needs nothing further. */
const LEAD_TONES: Record<LeadStatus, Tone> = {
  new: "pending",
  contacted: "accent",
  closed: "quiet",
};

export function LeadStatusBadge({ status }: { status: LeadStatus }) {
  return <Badge tone={LEAD_TONES[status]}>{status}</Badge>;
}

/**
 * `unpaid` is red rather than amber: past_due is a retry in progress, unpaid is
 * the biller having given up. They read the same at a glance and mean very
 * different things about whether anybody still needs to act.
 */
const SUBSCRIPTION_TONES: Record<SubscriptionStatus, Tone> = {
  trialing: "accent",
  active: "live",
  past_due: "pending",
  unpaid: "danger",
  canceled: "quiet",
};

export function SubscriptionStatusBadge({ status }: { status: SubscriptionStatus }) {
  return <Badge tone={SUBSCRIPTION_TONES[status]}>{status.replace("_", " ")}</Badge>;
}

/**
 * `proposed` is deliberately quiet rather than amber: a project nobody has
 * started is the ordinary state of new work, not something going wrong.
 * `suspended` is the only one that wants attention — it means somebody stopped
 * this on purpose and the reason is still outstanding.
 */
const PROJECT_TONES: Record<ProjectStatus, Tone> = {
  proposed: "quiet",
  in_progress: "accent",
  active: "live",
  suspended: "pending",
  ended: "quiet",
};

export function ProjectStatusBadge({ status }: { status: ProjectStatus }) {
  return <Badge tone={PROJECT_TONES[status]}>{status.replace("_", " ")}</Badge>;
}

/**
 * Deliberately not green until it is paid. An approved entry is still money
 * owed, and colouring it as settled is exactly the misreading that costs
 * someone their commission.
 */
const COMMISSION_TONES: Record<CommissionStatus, Tone> = {
  pending: "pending",
  approved: "accent",
  paid: "live",
  void: "quiet",
};

export function CommissionStatusBadge({ status }: { status: CommissionStatus }) {
  return <Badge tone={COMMISSION_TONES[status]}>{status}</Badge>;
}

/**
 * The title block every screen opens with. The action wraps under the title on
 * a narrow screen rather than squeezing it — a heading that has been crushed to
 * two words to make room for a button tells you less than the button does.
 */
export function PageHeader({
  title,
  lede,
  action,
}: {
  /**
   * A node rather than a string, because a detail screen's title is a licence
   * key or an order number and those are set in tabular figures — they are
   * compared by eye far more often than they are read.
   */
  title: ReactNode;
  lede?: ReactNode;
  action?: ReactNode;
}) {
  return (
    <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between sm:gap-4">
      <div className="min-w-0">
        <h1 className="text-lg font-semibold tracking-[-0.01em] text-ink sm:text-xl">{title}</h1>
        {lede !== undefined && <p className="mt-0.5 text-sm text-dim">{lede}</p>}
      </div>
      {action !== undefined && <div className="flex shrink-0 flex-wrap gap-2">{action}</div>}
    </header>
  );
}

export function Card({
  title,
  action,
  children,
  className,
}: {
  title?: string;
  action?: ReactNode;
  children: ReactNode;
  className?: string;
}) {
  return (
    <section className={cx("rounded-md border border-hairline bg-surface", className)}>
      {title !== undefined && (
        <header className="flex flex-wrap items-center justify-between gap-2 border-b border-hairline px-4 py-2.5">
          <h2 className="text-sm font-semibold text-ink">{title}</h2>
          {action}
        </header>
      )}
      {children}
    </section>
  );
}

/** A label and its value, for the definition lists on detail screens. */
export function DetailRow({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="flex items-start justify-between gap-4 px-4 py-2.5">
      <dt className="shrink-0 text-dim">{label}</dt>
      <dd className="text-right text-ink">{children}</dd>
    </div>
  );
}

export function EmptyState({ children }: { children: ReactNode }) {
  return <p className="px-4 py-8 text-center text-sm text-dim">{children}</p>;
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p
      role="alert"
      className="rounded-md border border-danger/40 bg-danger/10 px-3 py-2 text-sm text-danger"
    >
      {children}
    </p>
  );
}

/**
 * Wide content scrolls inside its own container. The page body must never
 * scroll sideways — on a laptop the activation table is wider than the shell.
 *
 * On a phone there is nothing to scroll: `Table` stacks each row into a card,
 * so the overflow only ever applies from the breakpoint up.
 */
export function TableWrap({ children }: { children: ReactNode }) {
  return <div className="md:overflow-x-auto">{children}</div>;
}

/**
 * Every data table in the app. Below `md` the rows become cards and each cell
 * shows the label its column header was carrying — see `.data-table` in
 * index.css.
 */
export function Table({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <table className={cx("data-table w-full border-collapse", className)}>{children}</table>
  );
}

export function Th({ children, className }: { children: ReactNode; className?: string }) {
  return (
    <th
      scope="col"
      className={cx(
        "whitespace-nowrap px-4 py-2 text-left text-[0.6875rem] font-medium uppercase tracking-[0.12em] text-dim",
        className,
      )}
    >
      {children}
    </th>
  );
}

export function Td({
  children,
  className,
  title,
  label,
}: {
  children: ReactNode;
  className?: string;
  /** Exact value behind a relative one, on hover. */
  title?: string;
  /**
   * The column this cell belongs to. Shown only once the table has stacked
   * into cards and the header row is gone; a value with no label on a phone is
   * a number nobody can identify.
   */
  label?: string;
}) {
  return (
    <td className={cx("px-4 py-2.5 align-middle text-sm", className)} title={title}>
      {/* `font-sans` explicitly: a cell carrying `tabular` sets a mono family,
          and without this the column labels on a phone would come out in mono
          on some rows and sans on others. */}
      {label !== undefined && (
        <span className="field-label shrink-0 font-sans md:hidden">{label}</span>
      )}
      {children}
    </td>
  );
}

export function formatDate(value: string | null | undefined): string {
  if (value === null || value === undefined) return "—";
  return new Date(value).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });
}

export function formatRelative(value: string | null | undefined): string {
  if (value === null || value === undefined) return "never";

  const seconds = Math.round((Date.now() - new Date(value).getTime()) / 1000);
  if (seconds < 90) return "just now";
  if (seconds < 3600) return `${Math.round(seconds / 60)} min ago`;
  if (seconds < 172_800) return `${Math.round(seconds / 3600)} h ago`;
  return `${Math.round(seconds / 86_400)} d ago`;
}

/**
 * Money is entered in whole currency, not cents. Asking for cents invites the
 * mistake where 4900 is typed meaning $4,900 and charged as $49 — a hundredfold
 * error that looks perfectly reasonable in the input.
 */
export function centsFromInput(value: string): number | undefined {
  const trimmed = value.trim();
  if (trimmed.length === 0) return undefined;
  const amount = Number(trimmed);
  if (!Number.isFinite(amount) || amount < 0) return undefined;
  return Math.round(amount * 100);
}

export function inputFromCents(cents: number | null | undefined): string {
  if (cents === null || cents === undefined) return "";
  return (cents / 100).toString();
}

export function formatMoney(cents: number | null, currency: string | null): string {
  if (cents === null) return "—";
  return new Intl.NumberFormat(undefined, {
    style: "currency",
    currency: currency ?? "USD",
  }).format(cents / 100);
}

/**
 * A number with a label beside it, big enough to read at a glance. Used
 * wherever a report or the dashboard shows a count rather than a table row —
 * one definition, so the two screens read as one visual language rather than
 * two that happen to agree today.
 */
export function Figure({
  label: name,
  hint,
  value,
}: {
  label: string;
  hint?: string;
  value: number | undefined;
}) {
  return (
    <div className="bg-surface px-4 py-3">
      <dt className="field-label">{name}</dt>
      <dd className="tabular text-2xl text-ink">{value ?? "—"}</dd>
      {hint !== undefined && <dd className="text-xs text-dim">{hint}</dd>}
    </div>
  );
}

/** "past_due" -> "past due". Statuses are stored as identifiers; this is the sentence form. */
export function statusLabel(status: string): string {
  return status.replace(/_/g, " ");
}
